// Tanstack Query client factory. Provider wraps the route tree in
// `src/routes/__root.tsx`. Defaults are intentionally conservative —
// staleTime of 30s for read queries, no refetch-on-focus by default,
// 0 retries (failures surface to the UI immediately so the engineer
// sees them in dev rather than masking via retry). Per-query overrides
// at the useQuery callsite.
//
// On the server the client must NOT be a process-wide singleton: the Node
// process is long-lived and handles many requests, and `routerWithQueryClient`
// mutates the client with per-response dehydration state. A shared client
// corrupts that bookkeeping across requests (intermittent `isDehydrated`
// errors) and would leak one tenant's SSR-cached data into another tenant's
// response. So the server gets a fresh client per call (per request); the
// browser keeps a singleton so the SSR cache hydrates into one client and
// the sign-out `.clear()` targets that same live cache.

import { QueryClient } from '@tanstack/react-query';

function makeQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 30_000,
				retry: 0,
				refetchOnWindowFocus: false,
			},
			mutations: {
				retry: 0,
			},
		},
	});
}

let browserClient: QueryClient | null = null;

export function getQueryClient(): QueryClient {
	if (typeof window === 'undefined') {
		return makeQueryClient();
	}
	if (!browserClient) {
		browserClient = makeQueryClient();
	}
	return browserClient;
}

export function resetQueryClientForTests(): void {
	browserClient = null;
}
